// =========================================================================
// Platform messaging pool — shared SendBlue number pool for Pro/Expert
// workspaces.
//
// Backed by `core.platform_messaging_numbers` (the pool itself) and
// `core.org_platform_messaging_assignments` (sticky-per-recipient
// routing), introduced in migration
// `20260702000000_messaging_lines_and_platform_pool.sql`.
//
// All admin RPCs gate on `core.is_platform_admin()` server-side; the
// service-layer calls here are thin wrappers around `core.rpc_admin_*`.
// Org members never touch this surface — they consume the pool through
// `core.fn_sms_resolve_sender` + `core.fn_allocate_platform_number`,
// which is invoked exclusively by the SMS pipeline.
//
// See `apps/admin/src/pages/MessagingPlatformPoolPage.tsx` for the
// platform-admin UI.
// =========================================================================

import { supabase } from '../client';

export type PlatformNumberStatus = 'active' | 'draining' | 'retired';

export interface PlatformNumberRow {
  id: string;
  phone_e164: string;
  provider: 'sendblue' | 'platform' | string;
  status: PlatformNumberStatus;
  sendblue_subaccount_ref: string | null;
  daily_new_contacts_count: number;
  daily_counter_reset_at: string;
  daily_cap: number;
  sticky_org_count: number;
  sticky_recipient_count: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformPoolHealth {
  total_numbers: number;
  active_numbers: number;
  draining_numbers: number;
  retired_numbers: number;
  total_daily_cap: number;
  total_used_today: number;
  headroom_pct: number;
}

// ---- admin reads -------------------------------------------------------

/**
 * Platform-admin only: list every number in the pool with today's
 * sticky-org / recipient counts. Backed by
 * `core.rpc_admin_list_platform_numbers()`.
 */
export async function adminListPlatformNumbers(): Promise<PlatformNumberRow[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_list_platform_numbers' as any);
  if (error) throw error;
  return (data ?? []) as unknown as PlatformNumberRow[];
}

/**
 * Snapshot of the pool's overall health — total numbers, today's
 * counters, headroom percentage. Surfaced both in the admin pool page
 * and (without admin gating) in Settings → Messaging so paid workspaces
 * can see the pool is alive. Backed by `core.rpc_platform_pool_health()`.
 */
export async function getPlatformPoolHealth(): Promise<PlatformPoolHealth> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_platform_pool_health' as any);
  if (error) throw error;
  const rows = (data ?? []) as unknown as PlatformPoolHealth[];
  if (rows.length === 0) {
    // Empty pool — show zeros rather than null so the UI doesn't have to
    // ternary-render every cell.
    return {
      total_numbers: 0,
      active_numbers: 0,
      draining_numbers: 0,
      retired_numbers: 0,
      total_daily_cap: 0,
      total_used_today: 0,
      headroom_pct: 0,
    };
  }
  // Defensive: even though rows.length is checked above, TS narrowing on
  // tuple-access is conservative. Fall back to the zero-row shape rather
  // than return undefined so callers don't have to ternary every cell.
  return (
    rows[0] ?? {
      total_numbers: 0,
      active_numbers: 0,
      draining_numbers: 0,
      retired_numbers: 0,
      total_daily_cap: 0,
      total_used_today: 0,
      headroom_pct: 0,
    }
  );
}

// ---- admin writes ------------------------------------------------------

export interface AddPlatformNumberInput {
  phoneE164: string;
  provider?: 'sendblue' | 'platform';
  sendblueSubaccountRef?: string | null;
  dailyCap?: number;
  notes?: string | null;
}

/**
 * Onboard a new number to the shared pool. The DB validates the E.164
 * format + daily_cap > 0; UI should still pre-flight to give immediate
 * feedback. Returns the new row's id.
 */
export async function adminAddPlatformNumber(input: AddPlatformNumberInput): Promise<string> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_add_platform_number' as any, {
      p_phone_e164: input.phoneE164,
      p_provider: input.provider ?? 'sendblue',
      p_sendblue_subaccount_ref: input.sendblueSubaccountRef ?? null,
      p_daily_cap: input.dailyCap ?? 50,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Flip a pool number between `active`, `draining`, and `retired`. Draining
 * stops new sticky assignments but lets existing sticky rows keep using
 * the number (so a customer never sees the sender flip). Retiring excludes
 * the number from `fn_allocate_platform_number` entirely.
 */
export async function adminSetPlatformNumberStatus(
  numberId: string,
  status: PlatformNumberStatus,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_set_platform_number_status' as any, {
      p_number_id: numberId,
      p_status: status,
    });
  if (error) throw error;
}

export interface UpdatePlatformNumberInput {
  numberId: string;
  dailyCap?: number | null;
  notes?: string | null;
  sendblueSubaccountRef?: string | null;
}

/**
 * Patch any of the operator-tunable fields on a pool number. Skipped
 * fields (left as `null`/undefined) are preserved server-side via
 * `coalesce(p_x, x)`.
 */
export async function adminUpdatePlatformNumber(
  input: UpdatePlatformNumberInput,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_admin_update_platform_number' as any, {
      p_number_id: input.numberId,
      p_daily_cap: input.dailyCap ?? null,
      p_notes: input.notes ?? null,
      p_subaccount_ref: input.sendblueSubaccountRef ?? null,
    });
  if (error) throw error;
}
